import api from './api';
import { TENANT_ID_STORAGE_KEY, getValidTenantId, ensureTenantId } from '../utils/tenantUtils';
import tenantCache from '../utils/tenantCache';

// Use the getValidTenantId function from tenantUtils
const getTenantId = () => getValidTenantId({ useLocalStorage: true });

// Create a public API instance without interceptors for public endpoints
const publicApi = api.create({
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Configure the api interceptor to automatically include tenant ID in headers
// Moving this to the top so it applies before any service methods are called
api.interceptors.request.use(config => {
  const tenantId = getTenantId();
  if (tenantId) {
    config.headers = config.headers || {};
    config.headers['X-Tenant-ID'] = tenantId;
    
    // Clean up any tenantId from query params to avoid duplication
    if (config.params && config.params.tenantId) {
      delete config.params.tenantId;
      // If params object is now empty, remove it
      if (Object.keys(config.params).length === 0) {
        delete config.params;
      }
    }
    
    // Handle cases where tenantId is in the URL
    if (config.url && config.url.includes('tenantId=')) {
      // Remove tenantId from URL query string
      config.url = config.url.replace(/([&?])tenantId=[^&]+(&|$)/, (match, prefix, suffix) => {
        return suffix === '&' ? prefix : '';  // Keep prefix if there are other params
      });
      // Clean up trailing ? or & if it's the last or only parameter
      config.url = config.url.replace(/[?&]$/, '');
    }
  }
  return config;
});

class TenantService {
  constructor() {
    // Initialize the tenant context on service creation
    this.ensureCurrentTenant();
  }

  // Helper method to configure request with proper tenant header
  _getConfigWithTenantHeader(config = {}) {
    const tenantId = getTenantId();
    return {
      ...config,
      headers: {
        ...config.headers,
        'X-Tenant-ID': tenantId
      }
    };
  }

  // Ensure we have a valid tenant context
  ensureCurrentTenant() {
    const tenantId = getTenantId();
    
    if (tenantId) {
      // If we have an ID but no tenant object, create a basic one
      const currentTenant = this.getCurrentTenant();
      if (!currentTenant) {
        this.setCurrentTenant({ _id: tenantId });
        return true;
      }
      return true;
    }
    return false;
  }

  // Check if a subdomain is available
  async checkSubdomainAvailability(subdomain) {
    try {
      console.log(`Checking availability for subdomain: ${subdomain}`);
      
      // Use the publicApi instance we created at the top
      const response = await publicApi.get(`/tenants/check-subdomain/${subdomain}`);
      console.log(`Subdomain check response:`, response.data);
      
      // Properly parse the response to ensure we're correctly interpreting the available flag
      if (response.data && typeof response.data.available === 'boolean') {
        return { 
          available: response.data.available,
          message: response.data.message || (response.data.available ? 
            'Subdomain is available' : 'Subdomain is already taken')
        };
      }
      
      // If we didn't get a proper response format, assume it's available
      return { 
        available: true,
        message: 'Subdomain is available'
      };
    } catch (error) {
      // If there's a 409 status, the subdomain is definitely taken
      if (error.response && error.response.status === 409) {
        return { available: false, message: 'Subdomain already taken' };
      }
      
      // For network errors or other issues, log and provide a clear response
      console.error('Error checking subdomain availability:', error);
      
      // If the error has a response, include some details
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      
      // Return a default "available" response to prevent blocking registration
      // when we can't properly check (better UX than blocking with an error)
      return { available: true, message: 'Could not verify subdomain availability, but you can proceed' };
    }
  }

  async getAllTenants() {
    return api.get('/tenants', this._getConfigWithTenantHeader());
  }
  
  async getTenantById(id) {
    // Check cache first to avoid unnecessary API calls
    const cachedTenant = tenantCache.getCachedTenantData();
    if (cachedTenant && (cachedTenant._id === id || cachedTenant.id === id)) {
      return { data: cachedTenant };
    }
    
    try {
      const response = await api.get(`/tenants/${id}`, this._getConfigWithTenantHeader());
      if (response.data) {
        tenantCache.cacheTenantData(response.data);
      }
      return response;
    } catch (error) {
      console.error(`Error fetching tenant with ID ${id}:`, error);
      
      // Return cached data even if expired as fallback
      const fallbackData = tenantCache.getCachedTenantData(true);
      if (fallbackData) {
        return { data: fallbackData, fromCache: true };
      }
      
      throw error;
    }
  }

  async getCurrentTenantDetails() {
    // Check if a request is already in progress
    const currentPromise = tenantCache.getFetchPromise();
    if (currentPromise) {
      return currentPromise;
    }
    
    try {
      // Check if we should throttle this request
      if (tenantCache.shouldThrottleRequest()) {
        console.log('Throttling tenant details request');
        
        // Return cached data if available
        const cachedData = tenantCache.getCachedTenantData(true);
        if (cachedData) {
          return { data: cachedData, fromCache: true };
        }
      }
      
      const tenantId = this.getCurrentTenantId();
      if (!tenantId) {
        console.warn('No tenant ID available for fetching tenant details');
        return { data: null };
      }
      
      // Check cache first (use fresh data only)
      const freshCachedData = tenantCache.getCachedTenantData(false);
      if (freshCachedData) {
        return { data: freshCachedData, fromCache: true };
      }
      
      // Create promise for the API call
      const fetchPromise = (async () => {
        try {
          // First try to use the new /current endpoint
          try {
            const response = await api.get('/tenants/current', this._getConfigWithTenantHeader());
            if (response?.data) {
              tenantCache.cacheTenantData(response.data);
            }
            return response;
          } catch (error) {
            console.warn('Failed to fetch tenant details using /current endpoint, falling back to /:id endpoint', error);
            // Fall back to the specific tenant ID endpoint if /current fails
            const idResponse = await api.get(`/tenants/${tenantId}`, this._getConfigWithTenantHeader());
            if (idResponse?.data) {
              tenantCache.cacheTenantData(idResponse.data);
            }
            return idResponse;
          }
        } catch (error) {
          console.error('Error in getCurrentTenantDetails:', error);
          
          // Return expired cached data as fallback
          const cachedData = tenantCache.getCachedTenantData(true);
          if (cachedData) {
            return { data: cachedData, fromCache: true };
          }
          
          throw error;
        } finally {
          // Clear promise reference regardless of outcome
          tenantCache.clearFetchPromise();
        }
      })();
      
      // Store the promise to deduplicate requests
      tenantCache.setFetchPromise(fetchPromise);
      
      return fetchPromise;
    } catch (error) {
      console.error('Error in getCurrentTenantDetails:', error);
      
      // Clear promise reference on error
      tenantCache.clearFetchPromise();
      
      // Always try to return something useful
      const cachedData = tenantCache.getCachedTenantData(true);
      if (cachedData) {
        return { data: cachedData, fromCache: true };
      }
      
      throw error;
    }
  }
  
  // Create a new tenant
  async createTenant(tenantData) {
    try {
      console.log('Creating new tenant:', tenantData.name);
      
      // Ensure we explicitly mark the user as an admin and set initial status
      const enhancedTenantData = {
        ...tenantData,
        role: tenantData.role || 'admin',  // Default to admin role if not specified
        status: 'pending',                 // Always set initial status to pending
        initialAdmin: true                 // Mark this user as the initial admin
      };
      
      // For the regular tenant creation process, we'll try with the standard API first
      try {
        const response = await publicApi.post('/tenants/register', enhancedTenantData);
        
        // Handle successful response and store tenant info
        this._handleSuccessfulTenantCreation(response);
        
        // Add notification of pending status to response
        return {
          ...response,
          data: {
            ...response.data,
            message: response.data.message || 'Organization registered successfully! Your admin account is pending approval.',
            status: 'pending'
          }
        };
      } catch (firstError) {
        // If regular tenant registration fails, try with alternative endpoints
        console.warn('Primary tenant registration failed:', firstError);
        
        try {
          // Show the user the appropriate message
          if (firstError.response?.status === 401) {
            throw {
              ...firstError,
              message: 'Registration requires admin approval. Please contact the administrator.'
            };
          }
          
          // Try with a different endpoint as fallback
          const response = await publicApi.post('/auth/register', {
            ...enhancedTenantData,
            tenantInfo: {
              name: enhancedTenantData.name,
              subdomain: enhancedTenantData.subdomain
            },
            // Explicitly pass admin role in case the fallback endpoint needs it differently
            role: 'admin',
            initialAdmin: true,
            status: 'pending'
          });
          
          this._handleSuccessfulTenantCreation(response);
          return response;
        } catch (secondError) {
          // If all attempts fail, throw a descriptive error
          console.error('All tenant registration attempts failed:', secondError);
          throw secondError;
        }
      }
    } catch (error) {
      console.error('Error in tenant creation:', error);
      throw error;
    }
  }
  
  // Helper method to handle successful tenant creation
  _handleSuccessfulTenantCreation(response) {
    if (!response || !response.data) return;
    
    const data = response.data;
    
    // Extract the tenant ID from various possible formats
    if (data._id || data.id || data.tenantId) {
      const tenantId = data._id || data.id || data.tenantId;
      console.log(`Tenant created successfully with ID: ${tenantId}`);
      
      // Store tenant info
      if (data.tenant) {
        this.setCurrentTenant(data.tenant);
        // Also cache it to prevent needless API calls
        tenantCache.cacheTenantData(data.tenant);
      } else {
        // If we just get an ID, create a basic tenant object
        const basicTenant = { 
          _id: tenantId,
          name: data.name || 'New Organization'
        };
        this.setCurrentTenant(basicTenant);
        tenantCache.cacheTenantData(basicTenant);
      }
    }
  }
  
  async updateTenant(id, tenantData) {
    return api.put(`/tenants/${id}`, tenantData, this._getConfigWithTenantHeader());
  }
  
  // Get the current tenant ID from storage using the shared function
  getCurrentTenantId() {
    // Check cache first for best performance
    const cachedId = tenantCache.getCachedTenantId();
    if (cachedId) return cachedId;
    
    // Fall back to traditional ID resolution
    const id = getTenantId();
    
    // Cache the ID for future use
    if (id) {
      tenantCache.cacheTenantId(id);
      
      // Create a basic tenant object if we have an ID but no object
      if (!this.getCurrentTenant()) {
        this.setCurrentTenant({ _id: id });
      }
    }
    
    return id;
  }
  
  // Fetch current tenant data from server or fallback to local storage
  async fetchCurrentTenant() {
    try {
      // Check if a request is already in progress
      const currentPromise = tenantCache.getFetchPromise();
      if (currentPromise) {
        return currentPromise;
      }
      
      // Check if we should throttle this request
      if (tenantCache.shouldThrottleRequest()) {
        // Return cached data if available (including expired)
        const cachedData = tenantCache.getCachedTenantData(true);
        if (cachedData) {
          return cachedData;
        }
      }
      
      const tenantId = this.getCurrentTenantId();
      
      if (!tenantId) {
        console.warn('No tenant ID available for fetching tenant details');
        return null;
      }
      
      // Check cache first before making API call
      const cachedData = tenantCache.getCachedTenantData(false);
      if (cachedData) {
        return cachedData;
      }
      
      // Create promise for the API call
      const fetchPromise = (async () => {
        try {
          const response = await this.getCurrentTenantDetails();
          if (response?.data && (response.data._id || response.data.id)) {
            // Store the fetched tenant data
            this.setCurrentTenant(response.data);
            
            // Also cache it in memory
            tenantCache.cacheTenantData(response.data);
            
            return response.data;
          }
        } catch (error) {
          console.warn('Failed to fetch tenant details from API, using local storage', error);
        } finally {
          // Clear promise reference regardless of outcome
          tenantCache.clearFetchPromise();
        }
        
        // Fallback to local storage
        return this.getCurrentTenant();
      })();
      
      // Store the promise to deduplicate requests
      tenantCache.setFetchPromise(fetchPromise);
      
      return fetchPromise;
    } catch (error) {
      console.error('Error in fetchCurrentTenant:', error);
      
      // Clear promise reference on error
      tenantCache.clearFetchPromise();
      
      return this.getCurrentTenant(); // Fallback to local
    }
  }
  
  // Get tenant API endpoints info for debugging
  getTenantEndpoints() {
    const tenantId = this.getCurrentTenantId() || 'unknown';
    return {
      updateTenant: `/tenants/${tenantId}`,
      updateSettings: `/tenants/${tenantId}/settings`
    };
  }
  
  // Get the current tenant object from storage
  getCurrentTenant() {
    // Check memory cache first (most efficient)
    const cachedTenant = tenantCache.getCachedTenantData(true);
    if (cachedTenant) {
      return cachedTenant;
    }
    
    // Fall back to localStorage
    const tenantString = localStorage.getItem('currentTenant');
    if (tenantString) {
      try {
        const data = JSON.parse(tenantString);
        
        // Cache this tenant data to avoid future localStorage reads
        if (data) tenantCache.cacheTenantData(data);
        
        return data;
      } catch (e) {
        console.error('Error parsing tenant from localStorage:', e);
      }
    }
    
    // If parsing failed or no tenant stored, return the ID as fallback
    const tenantId = getTenantId(); // Use direct function call to avoid recursion
    if (tenantId) {
      // Don't automatically create and save here to avoid recursion
      return { _id: tenantId };
    }
    return null;
  }
  
  // Set the current tenant ID in storage
  setCurrentTenantId(id) {
    if (!id) {
      console.warn('Attempted to set empty tenant ID');
      return;
    }
    
    // Cache the ID
    tenantCache.cacheTenantId(id);
    
    // Also update the current tenant object if it exists
    const currentTenant = this.getCurrentTenant();
    if (currentTenant) {
      currentTenant._id = id;
      localStorage.setItem('currentTenant', JSON.stringify(currentTenant));
    } else {
      // Create a basic tenant object with this ID
      const basicTenant = { _id: id };
      localStorage.setItem('currentTenant', JSON.stringify(basicTenant));
      
      // Also cache this basic tenant
      tenantCache.cacheTenantData(basicTenant);
    }
  }
  
  // Set the current tenant object in storage
  setCurrentTenant(tenant) {
    if (!tenant) {
      console.warn('Attempted to set null/undefined tenant');
      return;
    }
    
    if (tenant && (tenant._id || tenant.id)) {
      const tenantId = tenant._id || tenant.id;
      
      // Cache the tenant ID separately
      tenantCache.cacheTenantId(tenantId);
      
      // Ensure the tenant object uses _id consistently
      if (!tenant._id && tenant.id) {
        tenant._id = tenant.id;
      }
      
      // Preserve vital tenant settings during updates
      try {
        const existingTenant = this.getCurrentTenant();
        if (existingTenant) {
          // Ensure we don't lose settings that might not be included in the new data
          if (existingTenant.settings && !tenant.settings) {
            tenant.settings = existingTenant.settings;
          } else if (existingTenant.settings && tenant.settings) {
            // Deep merge settings to preserve all settings
            tenant.settings = {
              ...existingTenant.settings,
              ...tenant.settings
            };
          }
          
          // Preserve branding/customization if not in the new data
          if (existingTenant.primaryColor && !tenant.primaryColor) {
            tenant.primaryColor = existingTenant.primaryColor;
          }
          
          if (existingTenant.logo && !tenant.logo) {
            tenant.logo = existingTenant.logo;
          }
        }
      } catch (error) {
        console.warn('Error preserving existing tenant settings:', error);
      }
      
      // Cache the tenant data
      tenantCache.cacheTenantData(tenant);
      
      // Save tenant data to localStorage
      localStorage.setItem('currentTenant', JSON.stringify(tenant));
    } else {
      console.warn('Cannot set tenant without ID');
    }
  }
  
  // Get debug information about tenant context
  getTenantDebugInfo() {
    // Ensure we have at least a basic tenant object
    this.ensureCurrentTenant();
    
    const tenantId = this.getCurrentTenantId();
    const tenant = this.getCurrentTenant() || { id: "No ID", name: "No name", settings: {} };
    
    return {
      tenant: {
        id: tenant._id || tenant.id || "No ID",
        name: tenant.name || "No name",
        settings: tenant.settings || {}
      },
      apiUrl: '/tenants',
      endpoints: {
        updateTenant: `/tenants/${tenantId || 'unknown'}`,
        updateSettings: `/tenants/${tenantId || 'unknown'}/settings`
      },
      auth: {
        hasToken: !!localStorage.getItem('token'),
        tokenStart: localStorage.getItem('token')?.substring(0, 10) + '...' || 'No token',
        currentTenantId: tenantId || 'Not set'
      },
      cache: {
        enabled: true,
        hasCachedTenant: !!tenantCache.getCachedTenantData(true),
        hasCachedId: !!tenantCache.getCachedTenantId()
      }
    };
  }
  
  // Update tenant settings with improved error handling and caching
  async updateTenantSettings(tenantId, settingsData) {
    try {
      // Ensure we have a valid tenant context before proceeding
      this.ensureCurrentTenant();
      
      // Get tenant ID with fallback mechanisms
      const effectiveTenantId = tenantId || this.getCurrentTenantId();
      
      console.log(`Updating settings for tenant: ${effectiveTenantId || 'unknown'}`);
      
      if (!effectiveTenantId) {
        console.warn('No tenant ID available - saving settings locally only');
        // Instead of throwing error, continue with local-only changes
      }
      
      // Get current authentication state
      const token = localStorage.getItem('token');
      
      // Get current tenant for required field validation
      const currentTenant = this.getCurrentTenant();
      
      // Save settings to localStorage first to ensure we don't lose changes
      // even if the API call fails
      if (currentTenant) {
        const updatedTenant = this._mergeUpdatedSettings(currentTenant, settingsData);
        
        // Update both localStorage and memory cache immediately
        tenantCache.cacheTenantData(updatedTenant);
        this.setCurrentTenant(updatedTenant);
        
        console.log('Setting update saved to localStorage:', JSON.stringify({
          primaryColor: updatedTenant.primaryColor,
          settingsKeys: updatedTenant.settings ? Object.keys(updatedTenant.settings) : []
        }));
      }
      
      // If no token or no tenantId, just return the local-only changes
      if (!token || !effectiveTenantId) {
        console.warn('No authentication token or tenant ID available - saved settings locally only');
        return {
          status: 200,
          data: { success: true, localOnly: true },
          localOnly: true,
          status: 200
        };
      }
      
      // With token and tenantId, proceed with API call
      try {
        // Make the API request using the standard api service with proper tenant header
        const response = await api.patch(
          `/tenants/${effectiveTenantId}/settings`, 
          settingsData,
          this._getConfigWithTenantHeader()
        );
        
        console.log('Settings update successfully synced with server:', response);
        
        // Force refresh from server to ensure consistency
        try {
          await this.fetchCurrentTenant(true);
        } catch (refreshError) {
          console.warn('Error refreshing tenant data after update:', refreshError);
        }
        
        return response;
      } catch (apiError) {
        console.error('API error updating tenant settings:', apiError);
        
        // Check for validation errors
        if (apiError.response?.status === 400 && apiError.response?.data?.errors) {
          console.warn('Validation errors detected:', apiError.response.data.errors);
          throw apiError; // Re-throw validation errors so UI can handle them
        }
        
        // Return local-only response without triggering error
        return {
          status: 200,
          data: { 
            success: true, 
            localOnly: true,
            message: apiError.response?.status === 401 
              ? 'Authentication required for server update. Changes saved locally.' 
              : 'Settings updated locally due to server error.'
          },
          localOnly: true,
          originalError: apiError,
          errorStatus: apiError.response?.status
        };
      }
    } catch (error) {
      console.error('Error updating tenant settings:', error);
      throw error; // Re-throw the error to let the UI handle it
    }
  }

  // Helper method to merge settings updates
  _mergeUpdatedSettings(currentTenant, settingsData) {
    // Create a deep copy to avoid reference issues
    const updatedTenant = JSON.parse(JSON.stringify(currentTenant));
    
    // Update basic fields
    if (settingsData.name) updatedTenant.name = settingsData.name;
    if (settingsData.logo) updatedTenant.logo = settingsData.logo;
    if (settingsData.primaryColor) updatedTenant.primaryColor = settingsData.primaryColor;
    if (settingsData.adminEmail) updatedTenant.adminEmail = settingsData.adminEmail;
    
    // Update payment settings if provided
    if (settingsData.paymentSettings) {
      updatedTenant.paymentSettings = {
        ...(updatedTenant.paymentSettings || {}),
        ...settingsData.paymentSettings
      };
    }
    
    // Update settings object with deep merge
    if (settingsData.settings) {
      updatedTenant.settings = updatedTenant.settings || {};
      
      Object.keys(settingsData.settings).forEach(key => {
        if (typeof settingsData.settings[key] === 'object' && 
            settingsData.settings[key] !== null &&
            !Array.isArray(settingsData.settings[key])) {
          
          updatedTenant.settings[key] = {
            ...(updatedTenant.settings[key] || {}),
            ...settingsData.settings[key]
          };
        } else {
          updatedTenant.settings[key] = settingsData.settings[key];
        }
      });
    }
    
    return updatedTenant;
  }
  
  // Delete tenant
  async deleteTenant(id) {
    return api.delete(`/tenants/${id}`, this._getConfigWithTenantHeader());
  }
  
  // Initialize tenant context - call this on app startup
  async initTenantContext() {
    try {
      // Check if a proper initialization was already done
      if (tenantCache.getCachedTenantData(false)) {
        return this.getTenantDebugInfo();
      }
      
      // First ensure we have at least a basic tenant object
      this.ensureCurrentTenant();
      
      const tenantId = this.getCurrentTenantId();
      
      if (tenantId) {
        // Try to fetch full tenant details
        try {
          // For initialization, throttle less aggressively
          if (!tenantCache.shouldThrottleRequest()) {
            const response = await this.getCurrentTenantDetails();
            if (response?.data && (response.data._id || response.data.id)) {
              // Successfully retrieved tenant details
              this.setCurrentTenant(response.data);
              return this.getTenantDebugInfo();
            }
          } else {
            console.log('Throttling tenant initialization request');
          }
        } catch (error) {
          // For initialization, don't log full error details to reduce noise
        }
      }
      
      return this.getTenantDebugInfo();
    } catch (error) {
      // Log error but don't re-throw to prevent error cascades
      console.error('Error initializing tenant context:', error.message);
      return this.getTenantDebugInfo();
    }
  }

  // Setup super admin - first time system initialization
  async setupSuperAdmin(adminData) {
    try {
      console.log('Setting up super admin account...');
      
      // This is a special API call that doesn't need tenant headers
      const adminApi = api.create({
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      // Call the system setup endpoint
      const response = await adminApi.post('/admin/setup', adminData);
      
      if (response.data && response.data.token) {
        localStorage.setItem('token', response.data.token);
        
        if (response.data.tenant) {
          this.setCurrentTenant(response.data.tenant);
        }
        
        // Store admin status
        localStorage.setItem('isAdmin', 'true');
      }
      
      return response;
    } catch (error) {
      console.error('Error setting up super admin:', error);
      throw error;
    }
  }
  
  // Check if setup is needed
  async checkSystemStatus() {
    try {
      const response = await publicApi.get('/system/status');
      return response.data;
    } catch (error) {
      console.error('Error checking system status:', error);
      return {
        setupRequired: true,
        error: error.message
      };
    }
  }
  
  // Check if user is an admin
  isAdmin() {
    return localStorage.getItem('isAdmin') === 'true';
  }
  
  // Get list of all tenants (admin only)
  async getAllTenantsAdmin() {
    try {
      return await api.get('/admin/tenants');
    } catch (error) {
      console.error('Error getting all tenants list:', error);
      throw error;
    }
  }
  
  // Approve tenant registration (admin only)
  async approveTenant(tenantId) {
    try {
      return await api.post(`/admin/tenants/${tenantId}/approve`);
    } catch (error) {
      console.error(`Error approving tenant ${tenantId}:`, error);
      throw error;
    }
  }
  
  // Suspend tenant (admin only)
  async suspendTenant(tenantId, reason) {
    try {
      return await api.post(`/admin/tenants/${tenantId}/suspend`, { reason });
    } catch (error) {
      console.error(`Error suspending tenant ${tenantId}:`, error);
      throw error;
    }
  }

  // SuperAdmin methods for tenant management
  async getAllTenants() {
    try {
      // Use api instead of direct axios
      const response = await api.get(`/superadmin/tenants`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')}
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching tenants:', error);
      throw error;
    }
  }

  async getTenantById(id) {
    try {
      // Use api instead of direct axios
      const response = await api.get(`/superadmin/tenants/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')}
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching tenant with ID ${id}:`, error);
      throw error;
    }
  }

  async createTenant(tenantData) {
    try {
      // Add detailed debugging
      console.log('Creating tenant with data:', {
        ...tenantData,
        admin: tenantData.admin ? {
          ...tenantData.admin,
          password: '[REDACTED]' // Don't log actual password
        } : undefined
      });
      
      console.log(`Creating tenant at: /superadmin/tenants`);
      
      // Use api instead of direct axios
      const response = await api.post(`/superadmin/tenants`, tenantData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'x-auth-token': localStorage.getItem('token')}
      });
      
      console.log('Tenant creation response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error creating tenant:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response ? {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers
        } : 'No response'
      });
      throw error;
    }
  }

  async updateTenant(id, tenantData) {
    try {
      // Fix the API endpoint path
      const response = await api.put(`/superadmin/tenants/${id}`, tenantData);
      return response.data;
    } catch (error) {
      console.error(`Error updating tenant with ID ${id}:`, error);
      throw error;
    }
  }

  async deleteTenant(id) {
    try {
      // Fix the API endpoint path
      const response = await api.delete(`/superadmin/tenants/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting tenant with ID ${id}:`, error);
      throw error;
    }
  }

  async approveTenant(id) {
    try {
      // Fix the API endpoint path
      const response = await api.put(`/superadmin/tenants/${id}/approve`);
      return response.data;
    } catch (error) {
      console.error(`Error approving tenant with ID ${id}:`, error);
      throw error;
    }
  }

  async suspendTenant(id, reason) {
    try {
      // Fix the API endpoint path
      const response = await api.put(`/superadmin/tenants/${id}/suspend`, { reason });
      return response.data;
    } catch (error) {
      console.error(`Error suspending tenant with ID ${id}:`, error);
      throw error;
    }
  }

  async reactivateTenant(id) {
    try {
      // Fix the API endpoint path
      const response = await api.put(`/superadmin/tenants/${id}/reactivate`);
      return response.data;
    } catch (error) {
      console.error(`Error reactivating tenant with ID ${id}:`, error);
      throw error;
    }
  }

  async getTenantUsage(id) {
    try {
      // Fix the API endpoint path
      const response = await api.get(`/superadmin/tenants/${id}/usage`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching usage data for tenant with ID ${id}:`, error);
      throw error;
    }
  }

  async getTenantSubscription(id) {
    try {
      // Fix the API endpoint path
      const response = await api.get(`/superadmin/tenants/${id}/subscription`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching subscription data for tenant with ID ${id}:`, error);
      throw error;
    }
  }

  // Submit a tenant registration request that requires admin approval
  async submitTenantRequest(requestData) {
    try {
      console.log('Submitting tenant service request:', {
        name: requestData.name,
        subdomain: requestData.subdomain,
        phone: requestData.phone,
        location: requestData.location,
        industry: requestData.industry,
        companySize: requestData.companySize,
        requestedBy: requestData.requestedBy ? {
          ...requestData.requestedBy,
          password: '[REDACTED]' // Don't log actual password
        } : undefined,
        status: requestData.status
      });
      
      // Use the public API instance that doesn't include auth headers
      // This endpoint should be configured on the server to handle requests not tenants
      const response = await publicApi.post('/tenant-requests', requestData);
      
      console.log('Tenant request submitted successfully:', response.data);
      
      // Don't set tenant information since this is just a request
      // and not an actual tenant creation
      
      return response.data;
    } catch (error) {
      console.error('Error submitting tenant request:', error);
      
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      
      throw error;
    }
  }

  // Register new organization/company (public endpoint, no auth required)
  async registerCompany(tenantData) {
    try {
      console.log('Registering new company/tenant:', {
        name: tenantData.name,
        subdomain: tenantData.subdomain,
        admin: tenantData.admin ? {
          ...tenantData.admin,
          password: '[REDACTED]' // Don't log actual password
        } : undefined
      });
      
      // For backward compatibility, we'll try to use the submitTenantRequest method
      if (process.env.NODE_ENV !== 'production' || import.meta.env.VITE_TENANT_AS_REQUEST === 'true') {
        try {
          // Convert the format if necessary
          const requestData = {
            name: tenantData.name,
            subdomain: tenantData.subdomain,
            requestedBy: tenantData.admin || {}, // Map admin to requestedBy
            status: 'requested'
          };
          
          return await this.submitTenantRequest(requestData);
        } catch (requestError) {
          console.warn('Failed to submit as request, falling back to direct registration', requestError);
        }
      }
      
      // Use the public API instance that doesn't include auth headers
      const response = await publicApi.post('/tenants', tenantData);
      
      console.log('Company registration response:', response.data);
      
      // Handle successful tenant creation
      if (response.data && response.data.tenant) {
        this.setCurrentTenant(response.data.tenant);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error registering company/tenant:', error);
      
      if (error.response) {
        console.error('Server response:', {
          status: error.response.status,
          data: error.response.data
        });
      }
      
      throw error;
    }
  }
}

export default new TenantService();
export { TENANT_ID_STORAGE_KEY, getTenantId };